import React, { useState } from 'react';
import { Modal, Button, Form, Input, Select, Avatar, Radio } from 'antd';
// 使用 window.message 代替 antd 静态方法，避免 context 警告
import { TRACK_OPTIONS, CAREER_OPTIONS, MAJOR_OPTIONS, GRADE_OPTIONS } from '../../utils/constants';
import unionIcon from '../../styles/images/Union.png';
import useWorkspaceAuthModalStyles from '../../styles/components/modals/workspace-auth-modal';

const { Option } = Select;

const idTypeOptions = [
  { label: '身份证', value: '身份证' },
  { label: '护照', value: '护照' },
];

const WorkspaceAuthModal = ({ open, onClose, status = 'unverified', deadline = '8月10日24:00', members = [], onSuccess }) => {
  const { styles } = useWorkspaceAuthModalStyles();
  const [step, setStep] = useState(0); // 0: 状态, 1: 队伍, 2: 成员, 3: 确认
  const [teamInfo, setTeamInfo] = useState({});
  const [memberDetails, setMemberDetails] = useState([]);
  const [form1] = Form.useForm();
  const [form2] = Form.useForm();
  const [hasAdvisor, setHasAdvisor] = useState(false);

  // 关闭时重置
  React.useEffect(() => {
    if (!open) {
      setStep(0);
      setTeamInfo({});
      setMemberDetails([]);
      form1.resetFields();
      form2.resetFields();
      setHasAdvisor(false);
    }
  }, [open]);

  // Step1: 队伍信息
  const handleNextStep1 = async () => {
    try {
      const values = await form1.validateFields();
      setTeamInfo({ ...values, hasAdvisor });
      setStep(2);
    } catch {
      window.message?.error('请完善所有必填项');
    }
  };

  // Step2: 成员信息
  const handleNextStep2 = async () => {
    try {
      const values = await form2.validateFields();
      setMemberDetails(values.members);
      setStep(3);
    } catch {
      window.message?.error('请完善所有成员信息');
    }
  };

  // Step3: 提交
  const handleSubmit = () => {
    setStep(0);
    setTeamInfo({});
    setMemberDetails([]);
    window.message?.success('认证信息已提交！');
    onSuccess && onSuccess();
    onClose && onClose();
  };

  // 状态弹窗
  if (step === 0) {
    return (
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        closable={true}
        maskClosable={false}
        width={400}
        title={null}
      >
        <div className={styles.modalBody}>
          <img src={unionIcon} alt="认证icon" className={styles.icon} />
          {status === 'pending' ? (
            <div className={styles.title}>您已成功提交认证申请，请耐心等待审核结果</div>
          ) : (
            <>
              <div className={styles.title}>您的workspace还未认证为参赛队伍。</div>
              <div className={styles.desc}>请务必确认团队成员不会变动后再进行认证！<br/>提交认证后请勿增减团队成员，以免影响认证结果。</div>
              <Button type="primary" className={styles.primaryBtn} onClick={() => setStep(1)}>立即认证</Button>
              <div className={styles.deadline}>请在{deadline}之前完成认证</div>
            </>
          )}
        </div>
      </Modal>
    );
  }

  // Step1: 队伍信息
  if (step === 1) {
    return (
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        closable={true}
        maskClosable={false}
        width={500}
        title={null}
        destroyOnClose
      >
        <div className={styles.stepModalBody}>
          <Form form={form1} layout="vertical">
            <Form.Item label="队伍名称" name="teamName" rules={[{ required: true, message: '请输入队伍名称' }]}> 
              <Input placeholder="请输入队伍名称" maxLength={30} />
            </Form.Item>
            <Form.Item label="参赛场景" name="track" rules={[{ required: true, message: '请选择参赛场景' }]}> 
              <Select placeholder="请选择参赛场景">
                {TRACK_OPTIONS.map(opt => <Option key={opt} value={opt}>{opt}</Option>)}
              </Select>
            </Form.Item>
            <Form.Item label="是否有指导老师" name="hasAdvisor" rules={[{ required: true, message: '请选择' }]}> 
              <Select
                placeholder="请选择"
                onChange={v => setHasAdvisor(v === '是')}
              >
                <Option value="是">是</Option>
                <Option value="否">否</Option>
              </Select>
            </Form.Item>
            {hasAdvisor && (
              <>
                <Form.Item label="指导老师姓名" name="advisorName" rules={[{ required: true, message: '请输入指导老师姓名' }]}> 
                  <Input placeholder="请输入指导老师姓名" />
                </Form.Item>
                <Form.Item label="指导老师手机号码" name="advisorPhone" rules={[{ required: true, message: '请输入手机号码' }]}> 
                  <Input placeholder="请输入手机号码" />
                </Form.Item>
                <Form.Item label="指导老师邮箱地址" name="advisorEmail" rules={[{ required: true, message: '请输入邮箱地址' }]}> 
                  <Input placeholder="请输入邮箱地址" />
                </Form.Item>
              </>
            )}
            <div className={styles.stepTitle}>团队成员：</div>
            <div className={styles.stepDesc}>提交认证后将无法增减团队成员！请务必确认团队成员不会变动后再进行认证！</div>
            <div className={styles.memberList}>
              {(members || []).map(m => (
                <div key={m.id} className={styles.memberCard}>
                  <Avatar src={m.avatar} size={40} className={styles.memberAvatar} />
                  <div className={styles.memberName}>{m.name}</div>
                  {m.isLeader && <span className={styles.leader}>(队长)</span>}
                </div>
              ))}
            </div>
            <Button type="primary" block onClick={handleNextStep1} className={styles.primaryBtn}>下一步</Button>
          </Form>
        </div>
      </Modal>
    );
  }

  // Step2: 成员信息
  if (step === 2) {
    return (
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        closable={true}
        maskClosable={false}
        width={700}
        title={null}
        destroyOnClose
      >
        <div className={styles.stepModalBody}>
          <div className={styles.stepTitle}>成员详细信息</div>
          <div className={styles.stepDesc}>
            提交认证后将无法增减团队成员！请务必确认团队成员不会变动后再进行认证！
          </div>
          <Form form={form2} layout="vertical" initialValues={{ members: memberDetails.length ? memberDetails : (members || []) }}>
            {(members || []).map((m, idx) => (
              <div key={m.id} className={styles.memberFormCard}>
                <div className={styles.memberFormHeader}>
                  <Avatar src={m.avatar} size={40} className={styles.memberAvatar} />
                  <span style={{ fontWeight: 500 }}>{m.name}</span>
                  {m.isLeader && <span className={styles.leader}>(队长)</span>}
                </div>
                <Form.Item label="真实姓名" name={['members', idx, 'realName']} rules={[{ required: true, message: '请输入真实姓名' }]}> 
                  <Input placeholder="请输入真实姓名" />
                </Form.Item>
                <Form.Item label="性别" name={['members', idx, 'gender']} rules={[{ required: true, message: '请选择性别' }]}> 
                  <Radio.Group>
                    <Radio value="男">男</Radio>
                    <Radio value="女">女</Radio>
                  </Radio.Group>
                </Form.Item>
                <Form.Item label="团队职业" name={['members', idx, 'career']} rules={[{ required: true, message: '请选择团队职业' }]}> 
                  <Select placeholder="请选择团队职业">
                    {CAREER_OPTIONS.map(opt => <Option key={opt} value={opt}>{opt}</Option>)}
                  </Select>
                </Form.Item>
                <Form.Item label="专业" name={['members', idx, 'major']} rules={[{ required: true, message: '请选择专业' }]}> 
                  <Select placeholder="请选择专业">
                    {MAJOR_OPTIONS.map(opt => <Option key={opt} value={opt}>{opt}</Option>)}
                  </Select>
                </Form.Item>
                <Form.Item label="年级" name={['members', idx, 'grade']} rules={[{ required: true, message: '请选择年级' }]}> 
                  <Select placeholder="请选择年级">
                    {GRADE_OPTIONS.map(opt => <Option key={opt} value={opt}>{opt}</Option>)}
                  </Select>
                </Form.Item>
                <Form.Item label="在读学校" name={['members', idx, 'school']} rules={[{ required: true, message: '请输入在读学校' }]}> 
                  <Input placeholder="请输入在读学校" />
                </Form.Item>
                <Form.Item label="所在学校省市" name={['members', idx, 'schoolLocation']} rules={[{ required: true, message: '请输入省市' }]}> 
                  <Input placeholder="请输入省市，如：北京-北京" />
                </Form.Item>
                <Form.Item label="证件类型" name={['members', idx, 'idType']} rules={[{ required: true, message: '请选择证件类型' }]}> 
                  <Select placeholder="请选择证件类型">
                    {idTypeOptions.map(opt => <Option key={opt.value} value={opt.value}>{opt.label}</Option>)}
                  </Select>
                </Form.Item>
                <Form.Item label="证件号" name={['members', idx, 'idNumber']} rules={[{ required: true, message: '请输入证件号' }]}> 
                  <Input placeholder="请输入证件号" />
                </Form.Item>
              </div>
            ))}
            <div className={styles.stepFooter}>
              <Button onClick={() => setStep(1)}>上一步</Button>
              <Button type="primary" onClick={handleNextStep2} className={styles.primaryBtn}>下一步</Button>
            </div>
          </Form>
        </div>
      </Modal>
    );
  }

  // Step3: 信息确认
  if (step === 3) {
    return (
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        closable={true}
        maskClosable={false}
        width={800}
        title={null}
        destroyOnClose
      >
        <div className={styles.stepModalBody}>
          <div className={styles.stepTitle}>信息确认</div>
          <div className={styles.stepDesc}>
            提交认证后将无法增减团队成员！请务必确认团队成员不会变动后再进行认证！
          </div>
          <div style={{ marginBottom: 16 }}>
            <div>队伍名称：{teamInfo.teamName}</div>
            <div>参赛场景：{teamInfo.track}</div>
            <div>是否有指导老师：{teamInfo.hasAdvisor}</div>
            {teamInfo.hasAdvisor === '是' && (
              <>
                <div>指导老师姓名：{teamInfo.advisorName}</div>
                <div>指导老师手机号码：{teamInfo.advisorPhone}</div>
                <div>指导老师邮箱地址：{teamInfo.advisorEmail}</div>
              </>
            )}
          </div>
          <div className={styles.divider} />
          <div className={styles.stepTitle} style={{ marginBottom: 8 }}>成员详细信息</div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {(memberDetails.length ? memberDetails : (members || [])).map((m, idx) => (
              <div key={idx} className={styles.confirmCard}>
                <div className={styles.confirmHeader}>
                  <Avatar src={m.avatar} size={36} className={styles.memberAvatar} />
                  <span className={styles.confirmName}>{m.name}</span>
                  {m.isLeader && <span className={styles.confirmLeader}>(队长)</span>}
                </div>
                <div>真实姓名：{m.realName}</div>
                <div>性别：{m.gender}</div>
                <div>团队职业：{m.career}</div>
                <div>专业：{m.major}</div>
                <div>所在年级：{m.grade}</div>
                <div>在读学校：{m.school}</div>
                <div>所在学校省市：{m.schoolLocation}</div>
                <div>证件类型：{m.idType}</div>
                <div>证件号：{m.idNumber}</div>
              </div>
            ))}
          </div>
          <div className={styles.stepFooter}>
            <Button onClick={() => setStep(2)}>上一步</Button>
            <Button type="primary" onClick={handleSubmit} className={styles.primaryBtn}>确认无误，立即提交</Button>
          </div>
        </div>
      </Modal>
    );
  }

  return null;
};

export default WorkspaceAuthModal; 